import React, { useCallback, useEffect, useState } from "react";
import moment from "moment";
import { Button, Card, Col, Input, Row, Table } from "reactstrap";
import { apiSmsLogsGet } from "../../api/smsLogs";
import local from "../../localization/strings";
import Loader from "../common/Loader";

// "Free · exp 15 Apr 2027 · 2" — enough to tell two packages apart in a table cell.
const describePackage = (pkg) => {
	const type = pkg.isFreePackage ? local.TF_SmsPackageTypeFree : local.TF_SmsPackageTypePaid;
	const expiry = moment(pkg.expiryDate);
	// An overspend is charged to a package created already expired, which is what distinguishes it from
	// an ordinary draw and is worth saying rather than showing a date that reads as a mistake.
	const expiryLabel = expiry.isSameOrBefore(moment()) ? local.TF_SmsLogPackageExpired : expiry.format("DD MMM YYYY");
	return `${type} · ${expiryLabel} · ${pkg.segments}`;
};

const SiteSmsLog = ({ siteId }) => {
	const [logs, setLogs] = useState([]);
	const [totalCount, setTotalCount] = useState(0);
	const [showLoading, setShowLoading] = useState(true);
	const [hasError, setHasError] = useState(false);

	const [page, setPage] = useState(1);
	const pageSize = 25;
	// `search` is what the user is typing; `appliedSearch` is what the server has been asked for. Kept
	// apart so a query is not fired on every keystroke of a free-text search over message bodies.
	const [search, setSearch] = useState("");
	const [appliedSearch, setAppliedSearch] = useState("");
	const [dateFrom, setDateFrom] = useState("");
	const [dateTo, setDateTo] = useState("");

	const load = useCallback(async () => {
		setShowLoading(true);
		setHasError(false);
		try {
			const result = await apiSmsLogsGet(siteId, {
				skip: (page - 1) * pageSize,
				take: pageSize,
				search: appliedSearch || null,
				dateFrom: dateFrom || null,
				dateTo: dateTo || null,
			});
			setLogs((result && result.items) || []);
			setTotalCount((result && result.totalCount) || 0);
		} catch (error) {
			setHasError(true);
			setLogs([]);
			setTotalCount(0);
		}
		setShowLoading(false);
	}, [siteId, page, appliedSearch, dateFrom, dateTo]);

	useEffect(() => {
		load();
	}, [load]);

	const applySearch = () => {
		setPage(1);
		setAppliedSearch(search);
	};

	const clearFilters = () => {
		setPage(1);
		setSearch("");
		setAppliedSearch("");
		setDateFrom("");
		setDateTo("");
	};

	const hasFilters = !!appliedSearch || !!dateFrom || !!dateTo;
	const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
	const segmentsOnPage = logs.reduce((sum, item) => sum + item.segments, 0);

	return (
		<Card className="p-3">
			<Row form className="align-items-center">
				<Col md={4} className="mb-2">
					<Input
						type="text"
						value={search}
						placeholder={local.TF_SmsLogSearchPlaceholder}
						onChange={(e) => setSearch(e.target.value)}
						onKeyPress={(e) => {
							if (e.key === "Enter") applySearch();
						}}
					/>
				</Col>
				<Col md="auto" className="mb-2">
					<Button color="secondary" onClick={applySearch}>
						{local.TS_Search}
					</Button>
				</Col>
				<Col md={3} className="mb-2">
					<Input
						type="date"
						value={dateFrom}
						max={dateTo || undefined}
						onChange={(e) => {
							setPage(1);
							setDateFrom(e.target.value);
						}}
					/>
				</Col>
				<Col md={3} className="mb-2">
					<Input
						type="date"
						value={dateTo}
						min={dateFrom || undefined}
						onChange={(e) => {
							setPage(1);
							setDateTo(e.target.value);
						}}
					/>
				</Col>
				{hasFilters && (
					<Col md="auto" className="mb-2">
						<Button color="link" onClick={clearFilters}>
							{local.TF_SmsLogClearFilters}
						</Button>
					</Col>
				)}
			</Row>

			{showLoading ? (
				<Loader />
			) : hasError ? (
				<div className="p-2 text-danger">{local.TF_SmsLogError}</div>
			) : logs.length === 0 ? (
				<div className="p-2">{hasFilters ? local.TF_SmsLogNoneFiltered : local.TF_SmsLogNone}</div>
			) : (
				<>
					<Table className="table-hover table-sm mb-0 mt-2 fs--1 rounded-soft table-striped">
						<thead className="thead-light">
							<tr>
								<th className="text-nowrap">{local.TF_SmsLogDateSent}</th>
								<th className="text-nowrap">{local.TF_SmsLogRecipient}</th>
								<th>{local.TF_SmsSegments}</th>
								<th>{local.TF_SmsLogMessage}</th>
								<th className="d-none d-md-table-cell">{local.TF_SmsLogDrawnFrom}</th>
							</tr>
						</thead>
						<tbody>
							{logs.map((item) => (
								<tr key={item.id}>
									<td className="text-nowrap align-top">{moment(item.dateSent).format("DD MMM YYYY HH:mm")}</td>
									<td className="text-nowrap align-top">{item.mobileNumber}</td>
									<td className="align-top">{item.segments}</td>
									{/* Alarm bodies run to several lines and the dialog is not wide. Wrapping the whole
									    body keeps it readable without a per-row expander, and the message is the thing
									    being looked up. */}
									<td className="align-top" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
										{item.message}
									</td>
									<td className="d-none d-md-table-cell align-top">
										{!item.packages || item.packages.length === 0 ? (
											<span className="text-muted">{local.TF_SmsLogNoPackage}</span>
										) : (
											item.packages.map((pkg) => (
												<div key={pkg.packageId} className="text-nowrap" title={pkg.notes || undefined}>
													{describePackage(pkg)}
												</div>
											))
										)}
									</td>
								</tr>
							))}
						</tbody>
					</Table>

					<div className="d-flex justify-content-between align-items-center mt-2">
						<span className="fs--1 text-muted">
							{totalCount} {local.TF_SmsLogCount} · {segmentsOnPage} {local.TF_SmsLogSegmentsOnPage}
						</span>
						<span className="d-flex align-items-center">
							<Button className="btn-sm" color="secondary" disabled={page === 1} onClick={() => setPage(page - 1)}>
								{local.TF_SmsLogPrevious}
							</Button>
							<span className="fs--1 mx-2">
								{page} / {totalPages}
							</span>
							<Button className="btn-sm" color="secondary" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
								{local.TF_SmsLogNext}
							</Button>
						</span>
					</div>
				</>
			)}
		</Card>
	);
};

export default SiteSmsLog;
